import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getCategories, getProducts } from '@/lib/api';
import { useI18n } from '@/lib/i18n';
import { ArrowRight, ShoppingCart, Truck, CreditCard, Phone, Package } from 'lucide-react';

const HERO_IMG = "https://images.pexels.com/photos/32921658/pexels-photo-32921658.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const TRUCK_IMG = "https://customer-assets.emergentagent.com/job_rainforest-portal/artifacts/oa3385bi_AMAZONIA%20-%20Livraison%20Bruxelles.jpg";

export default function Home() {
  const [categories, setCategories] = useState([]);
  const [featured, setFeatured] = useState([]);
  const { t } = useI18n();

  useEffect(() => {
    getCategories().then(r => setCategories(r.data.filter(c => c.product_count > 0)));
    getProducts({ limit: 8 }).then(r => setFeatured(r.data.products));
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section className="relative h-[85vh] min-h-[600px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Latin food" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="max-w-2xl">
            <p className="text-xs uppercase tracking-[0.3em] text-[#F5C400] font-semibold mb-4">
              {t('hero_subtitle')}
            </p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-none mb-6">
              {t('hero_title_1')}<br />{t('hero_title_2')}<br />
              <span className="text-[#00A5FF]">{t('hero_title_3')}</span>
            </h1>
            <p className="text-base sm:text-lg text-white/80 mb-8 max-w-lg leading-relaxed">
              {t('hero_desc')}
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/catalog"
                data-testid="hero-catalog-cta"
                className="inline-flex items-center gap-2 px-6 py-3 bg-[#00A5FF] text-white font-semibold text-sm rounded-sm hover:bg-[#0090e0] hover:-translate-y-1 transition-all duration-200 shadow-lg"
              >
                Voir le catalogue <ArrowRight size={16} />
              </Link>
              <Link
                to="/avantages-pro"
                data-testid="hero-avantages-cta"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#111] font-semibold text-sm rounded-sm hover:bg-[#F8F9FA] hover:-translate-y-1 transition-all duration-200"
              >
                {t('hero_cta_advantages')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props */}
      <section className="border-b border-black/10">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4">
          {[
            { icon: ShoppingCart, label: t('vp_order'), desc: t('vp_order_desc') },
            { icon: Truck, label: t('vp_delivery'), desc: t('vp_delivery_desc') },
            { icon: CreditCard, label: t('vp_payment'), desc: t('vp_payment_desc') },
            { icon: Phone, label: t('vp_contact'), desc: t('vp_contact_desc') },
          ].map((item, i) => (
            <div key={i} className={`p-6 flex items-center gap-4 ${i < 3 ? 'border-r border-black/10' : ''}`}>
              <item.icon size={24} className="text-[#00A5FF] shrink-0" />
              <div>
                <p className="text-sm font-bold text-[#111]">{item.label}</p>
                <p className="text-xs text-black/50">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Categories Grid */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#00A5FF] font-semibold mb-2">{t('cat_title_sub')}</p>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#111] tracking-tight">
              {t('cat_title')}
            </h2>
          </div>
          <Link to="/catalog" className="hidden sm:flex items-center gap-1 text-sm font-medium text-[#00A5FF] hover:underline">
            {t('cat_see_all')} <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-px bg-black/10 border border-black/10">
          {categories.slice(0, 8).map((cat) => (
            <Link
              key={cat.id}
              to={`/catalog?category=${cat.slug}`}
              data-testid={`category-${cat.slug}`}
              className="group bg-white p-0 overflow-hidden"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={cat.image_url || 'https://images.pexels.com/photos/1640774/pexels-photo-1640774.jpeg?auto=compress&cs=tinysrgb&w=400'}
                  alt={cat.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-4 border-t border-black/10">
                <h3 className="text-sm font-bold text-[#111] group-hover:text-[#00A5FF] transition-colors duration-200">{cat.name}</h3>
                <p className="text-xs text-black/50 mt-0.5">{cat.product_count} produit{cat.product_count !== 1 ? 's' : ''}</p>
              </div>
            </Link>
          ))}
        </div>
        <div className="mt-4 sm:hidden text-center">
          <Link to="/catalog" className="text-sm font-medium text-[#00A5FF] hover:underline">
            Voir toutes les catégories
          </Link>
        </div>
      </section>

      {/* Featured Products */}
      <section className="bg-[#F8F9FA] border-t border-b border-black/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="flex items-end justify-between mb-8">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#E8231B] font-semibold mb-2">Sélection</p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#111] tracking-tight">
                Produits populaires
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 border border-black/10">
            {featured.map((p) => (
              <Link
                key={p.id}
                to={`/product/${p.id}`}
                data-testid={`product-card-${p.id}`}
                className="group bg-white p-4 hover:shadow-md transition-shadow duration-200"
              >
                <div className="aspect-square bg-[#F8F9FA] border border-black/5 mb-3 flex items-center justify-center overflow-hidden">
                  {p.image_url ? (
                    <img src={p.image_url} alt={p.name} className="w-full h-full object-cover" />
                  ) : (
                    <Package size={40} className="text-black/10" />
                  )}
                </div>
                <p className="text-xs text-[#00A5FF] font-semibold uppercase tracking-wider mb-1">{p.category}</p>
                <h3 className="text-sm font-bold text-[#111] leading-tight group-hover:text-[#00A5FF] transition-colors duration-200 line-clamp-2 mb-2">
                  {p.name}
                </h3>
                <div className="flex items-baseline gap-2">
                  <span className="text-lg font-extrabold text-[#111]">{p.public_price?.toFixed(2)}</span>
                  <span className="text-xs text-black/50">EUR</span>
                </div>
                {p.pro_price && (
                  <span className="inline-block mt-1 px-2 py-0.5 bg-[#F5C400]/15 text-[#111] text-xs font-semibold rounded-sm">
                    Pro: {p.pro_price.toFixed(2)} EUR
                  </span>
                )}
                {!p.pro_price && (
                  <p className="text-xs text-black/40 mt-1">Connectez-vous pour le prix pro</p>
                )}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Truck / Avantages Pro Section */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2">
            {/* Image side */}
            <div className="relative h-[400px] md:h-auto overflow-hidden">
              <img src={TRUCK_IMG} alt="Livraison Amazonia Bruxelles" className="w-full h-full object-cover" />
            </div>
            {/* Content side */}
            <div className="bg-[#111] p-8 md:p-12 lg:p-16 flex flex-col justify-center">
              <p className="text-xs uppercase tracking-[0.3em] text-[#F5C400] font-semibold mb-4">
                Livraison professionnelle
              </p>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight mb-4 leading-tight">
                Nous livrons les professionnels du Benelux
              </h2>
              <p className="text-base text-white/60 leading-relaxed mb-8">
                Amazonia livre directement sur votre lieu de travail avec des tarifs et conditions avantageuses.
              </p>
              <Link
                to="/avantages-pro"
                data-testid="cta-avantages-pro"
                className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#F5C400] text-[#111] font-bold text-sm rounded-sm hover:-translate-y-1 transition-all duration-200 self-start mb-6"
              >
                Découvrez tous nos avantages PRO <ArrowRight size={16} />
              </Link>
              <p className="text-sm text-white/50">
                Magasins, restauration, traiteurs et stands festival.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
